// @givmo/app-charity — payment setup (CPA-WEB4).
//
// Where a VERIFIED nonprofit sets up how it receives real grant money — the most sensitive UI in the
// portal. It orchestrates the §3.4 payment-data rails:
//   - RESOLVE the ACTIVE org + role from the active-org context (CPA-WEB3): a multi-org admin picks the
//     org in the shell switcher; single-org admins default to their one org. Payment binds to the ACTIVE
//     org (never `memberships[0]`) — the most sensitive screen must never edit a different charity's bank
//     details than the shell shows active. Payment is an OWNER-level action, so the mutating affordances
//     are gated on the active org's Owner role (matching the backend's OWNER-scoping); a non-Owner sees
//     the masked profile + an honest "ask an Owner" note, never the ACH/check actions they can't perform.
//   - LOAD the current MASKED profile (GET .../payment-profile) — null when nothing is set yet.
//   - CHOOSE a method (ACH or mailed check) when none is set, or MANAGE an existing one.
//   - ACH entry (<AchForm>) with masked confirm + honest name-match feedback (from the 201 result's
//     `flagged`/`message`); mailed-check election (<CheckElection>) to the read-only IRS address.
//   - BANK-CHANGE UX: changing bank details on an existing profile is behind a confirm dialog that
//     explains, BEFORE the change, what actually happens — the new account replaces the current one and
//     its holder name is checked against the org's legal name in the same request, so the outcome is
//     immediate: either the new account is verified, or it lands under review with payouts paused.
//   - HONEST status/flags everywhere — never implies money will flow when flagged or unrecognized.
//
// After any successful ACH submit / check election we invalidate the profile + events queries so the
// masked view + audit trail reflect the new honest state. The full account number is NEVER handled here —
// it lives only inside <AchForm> for the in-flight submit; this page only ever receives the masked result.
//
// TWO FACTS, decided at different times (payment-vocab.ts header). The name match resolves inside the
// submit request; whether grants can actually be sent by bank transfer (`transfers_enabled`) resolves
// LATER, when the backend's payee sync binds the profile — seconds after submit, in the background,
// sometimes delayed or failed. So the profile read POLLS for a bounded window while a verified ACH
// profile is not yet enabled (`TRANSFERS_POLL_*` below), and the card claims transfers only once the
// read says so. The submit ack for an auto-accepted ACH result says "verified", never "paid".
//
// ONE success surface. The <MaskedProfileView> state card is the confirmation — its badge and status
// note (`payment-vocab`'s verificationLabel/verificationDescription) carry the full outcome for every
// state, INCLUDING flagged, in copy richer than the server's one-line message. The submit result gets
// only a transient toast ("your click landed"); there is deliberately no persistent success banner,
// because a second green box restating the card reads as two separate confirmations and leaves a
// transient ack sitting on the page like a permanent fixture. FAILURES are the opposite and stay
// inline + persistent inside their own panel (`ach-form.tsx`'s `payment-ach-submit-error`,
// `check-election.tsx`'s error states) — a failure must never be dismissible-by-timeout.
//
// The root keeps `data-testid="page-payment"` (the data contract e2e/guard assert).

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  charityAdminGetCharityPaymentProfileOptions,
  charityAdminListCharityPaymentEventsOptions,
} from '@givmo/api-client/query';
import {
  isGivmoError,
  type CharityPaymentProfileOut,
  type CharityPaymentProfileResultOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Landmark,
  Lock,
  Mail,
  ShieldAlert,
} from 'lucide-react';
import { AchForm } from './ach-form';
import { CheckElection } from './check-election';
import { MaskedProfileView } from './masked-profile-view';
import { PaymentHistory } from './payment-history';
import {
  isAwaitingOwnerBankDetails,
  isAwaitingTransferSetup,
  isCheckMethod,
  isUnderReview,
  PAYMENT_METHOD,
  VERIFICATION_STATE,
} from './payment-vocab';
import { isOwnerRole } from '../admins/admins-roles';
import {
  useOptionalCharityConsole,
  type CharityConsoleState,
} from '../../console/charity-console-context';
import { useActiveOrg } from '../../org/active-org-context';

/** The Donations OWNER catalog's payment write (D-CP-42, CPA-BE28). */
export const CHARITY_PAYMENT_WRITE_PERMISSION = 'charity_payment:write';

/**
 * Whether this person may set up or change payment details.
 *
 * ⚠ THE EFFECTIVE SET DECIDES ONCE THE STANDING READ REPORTS THE CLASS'S OWN MODULE (D-CP-42:
 * `charity_payment:write` lives in the Donations OWNER catalog — a Global Admin holds it by tier, a
 * delegated Owner of Donations holds it by grant, and the server refuses everyone else). Before the
 * backend reports that module — a dark console, a read predating CPA-BE28 (the §9-14 fall-through),
 * a harness with no console at all — the legacy rule stands: the organization's Owner.
 */
export function canManagePayments(
  console: CharityConsoleState | null,
  membershipRole: string | null,
): boolean {
  const reported =
    console !== null && console.read.permissions !== null && !console.donationsFellThrough;
  if (reported) return console.read.permissions!.includes(CHARITY_PAYMENT_WRITE_PERMISSION);
  return membershipRole !== null && isOwnerRole(membershipRole);
}

const PREFIX = 'payment';

/**
 * How often the profile read re-polls while a verified ACH profile's transfers are not yet enabled.
 * The payee sync normally lands seconds after the submit commits; 5s keeps the card honest without
 * hammering the read.
 */
export const TRANSFERS_POLL_INTERVAL_MS = 5_000;
/**
 * How long the poll runs before it gives up and falls back to `refetchOnWindowFocus`. The window opens
 * at mount and re-opens on every successful submit. Two minutes covers a delayed sync; past that the
 * binding has most likely failed on our side, and a refetch loop cannot fix that — the next focus or
 * visit will still read the truth.
 */
export const TRANSFERS_POLL_WINDOW_MS = 120_000;

/**
 * The poll decision, factored out so the window arithmetic is unit-testable: the interval while the
 * profile is a verified-but-not-enabled ACH profile inside the window, `false` otherwise (no profile,
 * any other state, or the window has closed).
 */
export function transfersPollInterval(
  profile: CharityPaymentProfileOut | null | undefined,
  windowOpenedAt: number,
  now: number,
): number | false {
  if (!profile || !isAwaitingTransferSetup(profile)) return false;
  return now - windowOpenedAt < TRANSFERS_POLL_WINDOW_MS ? TRANSFERS_POLL_INTERVAL_MS : false;
}

/** The in-page flow state. `view` is the masked profile / choice; the others swap the panel in place. */
type PanelMode = 'view' | 'choose' | 'ach' | 'check';

export function PaymentPage() {
  const queryClient = useQueryClient();

  // ── Resolve the ACTIVE org + the write (active-org context; multi-org admins switch in the shell). ─
  //    Payment is the MOST sensitive screen — it binds to the org the shell shows active (never
  //    `memberships[0]`); the profile reads + ACH/check mutations below all key off `charityId`. The
  //    write itself is the Donations Owner catalog's string off the console's ONE standing read
  //    (`canManagePayments`), the membership's tier only while nothing is reported.
  const {
    activeMembership: membership,
    activeCharityId: charityId,
    isPending,
    isError,
    error,
    refetch,
  } = useActiveOrg();
  const console = useOptionalCharityConsole();
  const canManage = canManagePayments(console, membership?.role ?? null);
  // The Global Admin (the `Owner` role row), the one reader whose own entry re-binds the bank link
  // after a change of ownership (D-CP-101's floor; review-33's F2). The card words that state by it.
  const readerIsGlobalAdmin = isOwnerRole(membership?.role ?? null);

  // ── Load the current masked profile (only once we know the charity) ─────────────────────────────
  //    `verification_state` is decided in the submit request and never rewritten afterwards (the only
  //    later mutation is supersession by a NEW profile) — but `transfers_enabled` IS an outcome that
  //    lands later: the backend's payee sync binds a verified ACH profile seconds after the submit
  //    commits, and the profile read is the only place the portal learns it. So while the loaded
  //    profile is a verified ACH profile whose transfers are not yet enabled, the read re-polls every
  //    `TRANSFERS_POLL_INTERVAL_MS` for a `TRANSFERS_POLL_WINDOW_MS` window (opened at mount, re-opened
  //    on each successful submit), then stops and leaves `refetchOnWindowFocus` to keep it honest. A
  //    check election and an enabled profile never poll: nothing later can change them. A flagged
  //    profile does not poll either: what changes it is a reviewer's approval, hours or days away —
  //    and when that binds the profile the read answers `transfers_enabled: true` (with `flagged`
  //    still true), which the vocab renders as the enabled state (precedence, payment-vocab header);
  //    the focus refetch is the right cadence for that.
  const [pollWindowOpenedAt, setPollWindowOpenedAt] = useState(() => Date.now());
  const profileQuery = useQuery({
    ...charityAdminGetCharityPaymentProfileOptions({ path: { charity_id: charityId ?? '' } }),
    enabled: !!charityId,
    refetchOnWindowFocus: true,
    // Handed the RAW query state (no `select` here, so it is the wire shape). Re-evaluated after
    // every fetch and on every render, which is what lets a fresh window or a flipped member stop it.
    refetchInterval: (query) =>
      transfersPollInterval(query.state.data?.profile, pollWindowOpenedAt, Date.now()),
  });
  const profile: CharityPaymentProfileOut | null = profileQuery.data?.profile ?? null;

  const [panel, setPanel] = useState<PanelMode>('view');
  // Method chosen on the "choose a method" step (drives whether we render the ACH form or check election).
  const [chosenMethod, setChosenMethod] = useState<PaymentMethod>(PAYMENT_METHOD.ach);
  // The bank-change confirm dialog (only reached from an existing ACH profile).
  const [reverifyOpen, setReverifyOpen] = useState(false);

  const invalidatePaymentQueries = useMemo(
    () => async () => {
      if (!charityId) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: charityAdminGetCharityPaymentProfileOptions({
            path: { charity_id: charityId },
          }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: charityAdminListCharityPaymentEventsOptions({
            path: { charity_id: charityId },
          }).queryKey,
        }),
      ]);
    },
    [charityId, queryClient],
  );

  async function handleSubmitted(result: CharityPaymentProfileResultOut) {
    setPanel('view');
    setReverifyOpen(false);
    // A new election re-opens the transfers poll window: the submit response always carries
    // `transfers_enabled: false` (the payee sync runs after the commit), so the read that follows is
    // what will flip the card once the binding lands.
    setPollWindowOpenedAt(Date.now());
    // The invalidation resolves before the toast, so the state card below is already showing the new
    // election when the ack appears — the card IS the confirmation; this is only "your click landed".
    await invalidatePaymentQueries();
    // Honest confirmation toast — flagged results do NOT say "you're all set", and an auto-accepted
    // ACH result says "verified" only: the name matched, but whether transfers are enabled is a later
    // fact the card (not the toast) reports once the read carries it. Ruling 2026-09-18. (A submit
    // response never carries `transfers_enabled: true`, so `isUnderReview` and the raw flag agree
    // here; the presentation predicate is used all the same — precedence, payment-vocab header.)
    if (isUnderReview(result.profile)) {
      toast('Saved — our team is confirming the account-holder name.');
    } else if (result.profile.verification_state === VERIFICATION_STATE.autoAccepted) {
      toast.success('Bank details verified.');
    } else {
      toast.success('Payment details saved.');
    }
  }

  // ── Loading / error / no-org gates ──────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-memberships-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={
            isGivmoError(error)
              ? error.message
              : 'We couldn’t load your organization. Please try again.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={refetch}
              testId={`${PREFIX}-memberships-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  if (!membership || !charityId) {
    // No verified org yet → payment setup is unavailable; direct them to claim first.
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-no-org`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Claim your organization first"
          description="Once your organization is verified you can set up how it receives grant payments."
        />
      </PageShell>
    );
  }

  if (profileQuery.isPending) {
    return (
      <PageShell orgName={membership.name}>
        <Card className="p-24" testId={`${PREFIX}-profile-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (profileQuery.isError) {
    return (
      <PageShell orgName={membership.name}>
        <EmptyState
          testId={`${PREFIX}-profile-error`}
          icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
          title="Couldn’t load your payment details"
          description={
            isGivmoError(profileQuery.error)
              ? profileQuery.error.message
              : 'We couldn’t load your payment details. Please try again.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void profileQuery.refetch()}
              testId={`${PREFIX}-profile-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  // ── Without the write: read-only. The masked profile (if any) + an honest note naming who can. ────
  //    The org-level role reads "Global Admin" everywhere in console chrome and "Owner" names a
  //    per-module tier only (A6/R6) — so the note names both holders of the write, never "an
  //    organization Owner". After a change of ownership only a Global Admin's entry re-binds the
  //    link (D-CP-112), so there it points the reader at a Global Admin alone.
  if (!canManage) {
    const askWhom =
      profile && isAwaitingOwnerBankDetails(profile) ? 'a Global Admin' : 'one of them';
    return (
      <PageShell orgName={membership.name}>
        <div
          className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-14"
          {...testIdProp(`${PREFIX}-owner-only-note`)}
        >
          <Icon icon={Lock} size={18} className="mt-1 shrink-0 text-text-tertiary" aria-hidden />
          <p className="text-s13 text-text-secondary">
            Only a Global Admin or an Owner of Donations can set up or change payment details. You
            can view the current setup below — ask {askWhom} to make changes.
          </p>
        </div>
        {profile ? (
          <MaskedProfileView profile={profile} readerIsGlobalAdmin={readerIsGlobalAdmin} />
        ) : (
          <EmptyState
            testId={`${PREFIX}-empty-readonly`}
            icon={<Icon icon={Landmark} size={20} aria-hidden />}
            title="No payment method set up yet"
            description="Nobody has set up how this organization receives grant payments yet."
          />
        )}
        <PaymentHistory charityId={charityId} />
      </PageShell>
    );
  }

  // ── The writer's view ───────────────────────────────────────────────────────────────────────────
  return (
    <PageShell orgName={membership.name}>
      {panel === 'ach' ? (
        <AchForm
          charityId={charityId}
          isChange={!!profile && !isCheckMethod(profile.method)}
          isReentry={!!profile && isAwaitingOwnerBankDetails(profile)}
          onSubmitted={handleSubmitted}
          onCancel={() => setPanel('view')}
        />
      ) : panel === 'check' ? (
        <CheckElection
          charityId={charityId}
          onElected={handleSubmitted}
          onCancel={() => setPanel('view')}
        />
      ) : panel === 'choose' ? (
        <MethodChooser
          value={chosenMethod}
          onChange={setChosenMethod}
          onContinue={() => setPanel(chosenMethod)}
          onCancel={profile ? () => setPanel('view') : undefined}
        />
      ) : profile ? (
        // Existing profile → masked view + Owner management affordances.
        // The bank-change dialog protects an account grants are paid to. After a change of
        // ownership none is (CPA-BE69: grants already go by mailed check), so "Enter bank
        // details" opens the form directly; every other ACH profile keeps the dialog.
        <MaskedProfileView
          profile={profile}
          onChangeBank={
            isCheckMethod(profile.method)
              ? undefined
              : isAwaitingOwnerBankDetails(profile)
                ? () => setPanel('ach')
                : () => setReverifyOpen(true)
          }
          onChangeMethod={() => setPanel('choose')}
          readerIsGlobalAdmin={readerIsGlobalAdmin}
        />
      ) : (
        // No profile yet → invite the Owner to choose a method.
        <Card className="overflow-hidden">
          <EmptyState
            className="py-48"
            testId={`${PREFIX}-empty`}
            icon={<Icon icon={Landmark} size={22} aria-hidden />}
            title="Set up how you receive grants"
            description="Choose direct deposit for the fastest delivery, or receive a mailed check at your IRS address."
            action={
              <Button
                appearance="saas"
                tone="primary"
                size="lg"
                trailingIcon={<Icon icon={ArrowRight} size={16} aria-hidden />}
                onClick={() => {
                  setChosenMethod(PAYMENT_METHOD.ach);
                  setPanel('choose');
                }}
                testId={`${PREFIX}-setup-cta`}
              >
                Set up payments
              </Button>
            }
          />
        </Card>
      )}

      {/* Audit trail always visible under the active panel. */}
      {panel === 'view' ? <PaymentHistory charityId={charityId} /> : null}

      {/* Bank-change confirmation — explains what actually happens BEFORE the change. */}
      <Dialog.Root open={reverifyOpen} onOpenChange={setReverifyOpen}>
        <Dialog.Content aria-label="Change bank details" testId={`${PREFIX}-reverify-dialog`}>
          <div className="flex flex-col gap-16">
            <div className="flex items-start gap-12">
              <span className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-warning-50 text-warning-600">
                <Icon icon={AlertTriangle} size={20} aria-hidden />
              </span>
              <div className="flex flex-col gap-4">
                <Dialog.Title className="font-w700 text-h6 text-text-strong">
                  Changing your bank details replaces your current account
                </Dialog.Title>
                <Dialog.Description className="text-s14 text-text-secondary">
                  Your new account replaces the one on file, and we check its account-holder name
                  against your organization’s legal name straight away. If the name doesn’t match,
                  we pause grant payouts while our team confirms it. We’ll also notify your
                  organization’s admins that the bank details changed. Continue?
                </Dialog.Description>
              </div>
            </div>
            <div className="flex items-center justify-end gap-8">
              <Dialog.Close
                render={
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    testId={`${PREFIX}-reverify-cancel`}
                  >
                    Keep current details
                  </Button>
                }
              />
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                onClick={() => {
                  setReverifyOpen(false);
                  setPanel('ach');
                }}
                testId={`${PREFIX}-reverify-confirm`}
              >
                Continue to change
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </PageShell>
  );
}

// ── Method chooser (ACH vs mailed check) ───────────────────────────────────────────────────────────
function MethodChooser({
  value,
  onChange,
  onContinue,
  onCancel,
}: {
  value: PaymentMethod;
  onChange: (value: PaymentMethod) => void;
  onContinue: () => void;
  onCancel?: () => void;
}) {
  return (
    <Card className="overflow-hidden" testId={`${PREFIX}-method-chooser`}>
      <div className="border-b border-border-subtle px-24 py-20">
        <div className="flex items-start gap-12">
          <span className="flex size-[40px] shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Icon icon={Landmark} size={20} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-s11 font-w600 uppercase tracking-wide text-brand-700">
              Payment setup · Step 1 of 2
            </p>
            <h2 className="mt-2 text-s18 font-w700 text-text-strong">
              Choose how you’ll receive grants
            </h2>
            <p className="mt-4 text-s13 text-text-secondary">
              Select the delivery method that works best for your organization. You can change it
              later.
            </p>
          </div>
        </div>
      </div>
      <CardBody className="flex flex-col gap-20 px-24 py-24">
        <fieldset
          role="radiogroup"
          aria-label="Payment method"
          className="grid grid-cols-1 gap-12 sm:grid-cols-2"
          {...testIdProp(`${PREFIX}-method-group`)}
        >
          <PaymentMethodCard
            value={PAYMENT_METHOD.ach}
            checked={value === PAYMENT_METHOD.ach}
            onChange={() => onChange(PAYMENT_METHOD.ach)}
            icon={Landmark}
            title="Direct deposit (ACH)"
            description="Funds arrive securely in your bank account."
            badge="Fastest"
            testId={`${PREFIX}-method-ach`}
          />
          <PaymentMethodCard
            value={PAYMENT_METHOD.check}
            checked={value === PAYMENT_METHOD.check}
            onChange={() => onChange(PAYMENT_METHOD.check)}
            icon={Mail}
            title="Mailed check"
            description="A check is mailed to your organization’s IRS address on file."
            testId={`${PREFIX}-method-check`}
          />
        </fieldset>
        <div className="flex flex-col-reverse items-stretch gap-8 border-t border-border-subtle pt-20 sm:flex-row sm:items-center sm:justify-end">
          {onCancel ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="lg"
              onClick={onCancel}
              testId={`${PREFIX}-method-cancel`}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            trailingIcon={<Icon icon={ArrowRight} size={16} aria-hidden />}
            onClick={onContinue}
            testId={`${PREFIX}-method-continue`}
          >
            Continue with {isCheckMethod(value) ? 'mailed check' : 'direct deposit'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/** The chooser's selection. Same values the wire uses, so it feeds `payment-vocab` helpers directly. */
type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];
type PaymentMethodIcon = typeof Landmark;

function PaymentMethodCard({
  value,
  checked,
  onChange,
  icon,
  title,
  description,
  badge,
  testId,
}: {
  value: PaymentMethod;
  checked: boolean;
  onChange: () => void;
  icon: PaymentMethodIcon;
  title: string;
  description: string;
  badge?: string;
  testId: string;
}) {
  return (
    <label
      className={[
        'relative flex min-h-[132px] cursor-pointer items-start gap-14 rounded-xl border p-18',
        'transition-[background-color,border-color,box-shadow] duration-150',
        checked
          ? 'border-brand-500 bg-brand-50 shadow-xs'
          : 'border-border bg-surface hover:border-neutral-400 hover:bg-surface-hover',
      ].join(' ')}
    >
      <input
        type="radio"
        name="payment-method"
        value={value}
        checked={checked}
        onChange={onChange}
        className="peer sr-only"
        {...testIdProp(testId)}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-xl peer-focus-visible:shadow-ring"
      />
      <span
        className={[
          'relative flex size-[40px] shrink-0 items-center justify-center rounded-lg',
          checked ? 'bg-brand-100 text-brand-700' : 'bg-neutral-100 text-icon',
        ].join(' ')}
      >
        <Icon icon={icon} size={20} aria-hidden />
      </span>
      <span className="relative min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-8">
          <span className="text-s14 font-w600 text-text-strong">{title}</span>
          {badge ? (
            <Badge className="border border-brand-300 bg-white" tone="brand" size="sm">
              {badge}
            </Badge>
          ) : null}
        </span>
        <span className="mt-6 block text-s13 leading-h13 text-text-secondary">{description}</span>
      </span>
      <span
        aria-hidden
        className={[
          'relative mt-2 flex size-18 shrink-0 items-center justify-center rounded-full border-2',
          checked ? 'border-brand-600 bg-brand-600' : 'border-neutral-400 bg-surface',
        ].join(' ')}
      >
        {checked ? <span className="size-6 rounded-full bg-white" /> : null}
      </span>
    </label>
  );
}

// ── Page frame ──────────────────────────────────────────────────────────────────────────────────────
//    No organization chip in the header: the shell's sidebar chip and account menu already name the
//    organization, and a second chip one column to the right printed it twice in one viewport (the
//    OM-14 mockup pass's finding). `orgName` still gates the trust note, which renders only once an
//    organization has resolved.
function PageShell({ children, orgName }: { children: React.ReactNode; orgName?: string }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[800px] flex-col gap-20 px-20 py-28 sm:px-24 sm:py-32"
      {...testIdProp('page-payment')}
    >
      <PageHeader
        title="Payments"
        subtitle="Manage the secure delivery of grants to your organization."
      />
      {orgName ? (
        <div
          className="flex items-center gap-8 rounded-lg border border-brand-100 bg-brand-50 px-12 py-10 text-s12 text-brand-800"
          {...testIdProp(`${PREFIX}-trust-note`)}
        >
          <Icon icon={Lock} size={15} className="shrink-0 text-brand-600" aria-hidden />
          <p>Bank details are encrypted and used only to send your grants.</p>
        </div>
      ) : null}
      {children}
    </div>
  );
}
